import React, { useState, useEffect, useMemo, memo } from 'react';
import ReactDOM from 'react-dom';
import { useLists } from '../../contexts/ListsContext';
import { useCollections } from '../../contexts/CollectionsContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useModalClose } from '../../hooks/useModalClose';
import ListHeroPhoto from '../ListHeroPhoto';
import './CreateCollectionModal.css';

const CreateCollectionModal = memo(function CreateCollectionModal({
  isOpen,
  onClose,
  onSuccess,
}) {
  const { lists, collaborationLists } = useLists();
  const { createCollection } = useCollections();
  const { isClosing, handleClose } = useModalClose(onClose);

  // State
  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [selectedListIds, setSelectedListIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Handle escape key
  useEscapeKey(isOpen, handleClose);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setVisibility('private');
      setSelectedListIds(new Set());
      setSearchQuery('');
      setError(null);
    }
  }, [isOpen]);

  // Combine, sort alphabetically, and filter available lists
  const availableLists = useMemo(() => {
    const allLists = [...lists, ...collaborationLists];

    // Sort alphabetically by title
    allLists.sort((a, b) => {
      const titleA = (a.title || a.name || '').toLowerCase();
      const titleB = (b.title || b.name || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });

    if (!searchQuery.trim()) {
      return allLists;
    }

    const query = searchQuery.toLowerCase().trim();
    return allLists.filter((list) => {
      const title = (list.title || list.name || '').toLowerCase();
      const location = list.location?.display?.toLowerCase() || '';
      const category = list.category?.toLowerCase() || '';
      return (
        title.includes(query) ||
        location.includes(query) ||
        category.includes(query)
      );
    });
  }, [lists, collaborationLists, searchQuery]);

  // Toggle list selection
  const toggleListSelection = (listId) => {
    setSelectedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) {
        next.delete(listId);
      } else {
        next.add(listId);
      }
      return next;
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!title.trim()) {
      setError('Please enter a collection name');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const result = await createCollection({
        title: title.trim(),
        visibility,
        listIds: [...selectedListIds],
      });

      if (result.success) {
        onSuccess?.(result.data);
        handleClose();
      } else {
        setError(result.error || 'Failed to create collection');
      }
    } catch (err) {
      console.error('Failed to create collection:', err);
      setError('Failed to create collection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="collection-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="collection-modal-title"
    >
      <div
        className={`collection-modal ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="collection-modal-header">
          <h2 id="collection-modal-title">
            <span className="collection-modal-title-text">New Collection</span>
          </h2>
          <button
            className="collection-modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="collection-modal-content">
          {/* Title input */}
          <div className="collection-form-group">
            <label htmlFor="collection-title" className="collection-form-label">
              Name
            </label>
            <input
              id="collection-title"
              type="text"
              className="collection-form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Trip Ideas, Date Spots"
              maxLength={100}
              autoFocus
            />
          </div>

          {/* Visibility toggle */}
          <div className="collection-form-group">
            <label className="collection-form-label">Visibility</label>
            <div className="collection-visibility-options">
              <button
                type="button"
                className={`collection-visibility-option ${visibility === 'private' ? 'active' : ''}`}
                onClick={() => setVisibility('private')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Private
              </button>
              <button
                type="button"
                className={`collection-visibility-option ${visibility === 'public' ? 'active' : ''}`}
                onClick={() => setVisibility('public')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                Public
              </button>
            </div>
          </div>

          {/* List picker */}
          <div className="collection-form-group">
            <label className="collection-form-label">
              Add Lists
              {selectedListIds.size > 0 && (
                <span className="collection-selected-count">
                  {selectedListIds.size} selected
                </span>
              )}
            </label>

            {/* Search */}
            <div className="collection-search-wrapper">
              <svg className="collection-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                className="collection-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search lists..."
              />
              {searchQuery && (
                <button
                  className="collection-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            {/* Lists */}
            <div className="collection-lists-picker">
              {availableLists.length === 0 ? (
                <div className="collection-lists-empty">
                  {searchQuery ? 'No lists match your search' : 'No lists available'}
                </div>
              ) : (
                availableLists.map((list) => {
                  const isSelected = selectedListIds.has(list.id);
                  const heroItem = (list.items || []).find((item) => item.photoUrl);

                  return (
                    <button
                      key={list.id}
                      type="button"
                      className={`collection-list-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleListSelection(list.id)}
                    >
                      <div className="collection-list-item-photo">
                        <ListHeroPhoto
                          placeId={heroItem?.placeId}
                          storedPhotoUrl={heroItem?.photoUrl}
                          customPhoto={list.customPhoto}
                          altText={list.title || list.name}
                        />
                      </div>
                      <div className="collection-list-item-info">
                        <span className="collection-list-item-title">
                          {list.title || list.name}
                        </span>
                        <span className="collection-list-item-meta">
                          {list.items?.length || list.itemCount || 0} places
                        </span>
                      </div>
                      <div className={`collection-list-item-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="collection-modal-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="collection-modal-footer">
          <button
            type="button"
            className="collection-modal-btn collection-modal-btn-cancel"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="collection-modal-btn collection-modal-btn-save"
            onClick={handleSave}
            disabled={saving || !title.trim()}
          >
            {saving ? 'Creating...' : 'Create Collection'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
});

export default CreateCollectionModal;
